"use client"

import { useState, useCallback, useRef } from "react"
import type { Layer, DrawObject, Tool, Point, PCBProject, LineStyle, ProjectProperties } from "@/lib/pcb-types"
import { LAYER_COLORS, serializeProject, deserializeProject, DEFAULT_PROJECT_PROPERTIES } from "@/lib/pcb-types"

function generateId(): string {
  return Math.random().toString(36).substring(2, 9) + Date.now().toString(36)
}

const defaultLayer: Layer = {
  id: generateId(),
  name: "Top Copper",
  color: LAYER_COLORS[0],
  visible: true,
  locked: false,
  opacity: 1,
  objects: [],
}

const initialState: PCBProject = {
  layers: [defaultLayer],
  activeLayerId: defaultLayer.id,
  activeTool: "select",
  activeColor: LAYER_COLORS[0],
  strokeWidth: 2,
  activeLineStyle: "solid" as LineStyle,
  imageGrayscale: 0,
  imageTint: 0,
  imageOverlay: "",
  imageOverlayOpacity: 0,
  pcbImage: null,
  zoom: 1,
  panOffset: { x: 0, y: 0 },
  projectProperties: { ...DEFAULT_PROJECT_PROPERTIES },
}

const MAX_UNDO = 50

export function usePCBStore() {
  const [state, setState] = useState<PCBProject>(initialState)

  // Undo / Redo stacks store clones of layers
  const undoStack = useRef<Layer[][]>([])
  const redoStack = useRef<Layer[][]>([])

  // Push current layers onto undo stack before mutation
  const pushUndo = useCallback(() => {
    setState((prev) => {
      undoStack.current.push(JSON.parse(JSON.stringify(prev.layers)))
      if (undoStack.current.length > MAX_UNDO) undoStack.current.shift()
      redoStack.current = []
      return prev
    })
  }, [])

  const undo = useCallback(() => {
    setState((prev) => {
      if (undoStack.current.length === 0) return prev
      redoStack.current.push(JSON.parse(JSON.stringify(prev.layers)))
      const restored = undoStack.current.pop()!
      return { ...prev, layers: restored, activeLayerId: restored[0]?.id ?? null }
    })
  }, [])

  const redo = useCallback(() => {
    setState((prev) => {
      if (redoStack.current.length === 0) return prev
      undoStack.current.push(JSON.parse(JSON.stringify(prev.layers)))
      const restored = redoStack.current.pop()!
      return { ...prev, layers: restored, activeLayerId: restored[0]?.id ?? null }
    })
  }, [])

  const canUndo = undoStack.current.length > 0
  const canRedo = redoStack.current.length > 0

  const setActiveTool = useCallback((tool: Tool) => {
    setState((prev) => ({ ...prev, activeTool: tool }))
  }, [])

  const setActiveColor = useCallback((color: string) => {
    setState((prev) => ({ ...prev, activeColor: color }))
  }, [])

  const setStrokeWidth = useCallback((width: number) => {
    setState((prev) => ({ ...prev, strokeWidth: width }))
  }, [])

  const setActiveLineStyle = useCallback((lineStyle: LineStyle) => {
    setState((prev) => ({ ...prev, activeLineStyle: lineStyle }))
  }, [])

  const setImageGrayscale = useCallback((value: number) => {
    setState((prev) => ({ ...prev, imageGrayscale: Math.max(0, Math.min(100, value)) }))
  }, [])

  const setImageTint = useCallback((value: number) => {
    setState((prev) => ({ ...prev, imageTint: Math.max(0, Math.min(360, value)) }))
  }, [])

  const setImageOverlay = useCallback((color: string) => {
    setState((prev) => ({ ...prev, imageOverlay: color }))
  }, [])

  const setImageOverlayOpacity = useCallback((value: number) => {
    setState((prev) => ({ ...prev, imageOverlayOpacity: Math.max(0, Math.min(100, value)) }))
  }, [])

  const setActiveLayer = useCallback((layerId: string) => {
    setState((prev) => {
      const layer = prev.layers.find((l) => l.id === layerId)
      return {
        ...prev,
        activeLayerId: layerId,
        activeColor: layer?.color ?? prev.activeColor,
      }
    })
  }, [])

  const addLayer = useCallback((name?: string) => {
    pushUndo()
    setState((prev) => {
      const usedColors = prev.layers.map((l) => l.color)
      const availableColor =
        LAYER_COLORS.find((c) => !usedColors.includes(c)) ??
        LAYER_COLORS[prev.layers.length % LAYER_COLORS.length]
      const newLayer: Layer = {
        id: generateId(),
        name: name ?? `Layer ${prev.layers.length + 1}`,
        color: availableColor,
        visible: true,
        locked: false,
        opacity: 1,
        objects: [],
      }
      return {
        ...prev,
        layers: [...prev.layers, newLayer],
        activeLayerId: newLayer.id,
        activeColor: newLayer.color,
      }
    })
  }, [pushUndo])

  const removeLayer = useCallback((layerId: string) => {
    pushUndo()
    setState((prev) => {
      if (prev.layers.length <= 1) return prev
      const newLayers = prev.layers.filter((l) => l.id !== layerId)
      const newActive =
        prev.activeLayerId === layerId ? newLayers[0].id : prev.activeLayerId
      return {
        ...prev,
        layers: newLayers,
        activeLayerId: newActive,
      }
    })
  }, [pushUndo])

  const renameLayer = useCallback((layerId: string, name: string) => {
    setState((prev) => ({
      ...prev,
      layers: prev.layers.map((l) => (l.id === layerId ? { ...l, name } : l)),
    }))
  }, [])

  const toggleLayerVisibility = useCallback((layerId: string) => {
    setState((prev) => ({
      ...prev,
      layers: prev.layers.map((l) =>
        l.id === layerId ? { ...l, visible: !l.visible } : l
      ),
    }))
  }, [])

  const toggleLayerLock = useCallback((layerId: string) => {
    setState((prev) => ({
      ...prev,
      layers: prev.layers.map((l) =>
        l.id === layerId ? { ...l, locked: !l.locked } : l
      ),
    }))
  }, [])

  const setLayerOpacity = useCallback((layerId: string, opacity: number) => {
    setState((prev) => ({
      ...prev,
      layers: prev.layers.map((l) =>
        l.id === layerId ? { ...l, opacity } : l
      ),
    }))
  }, [])

  const setLayerColor = useCallback((layerId: string, color: string) => {
    setState((prev) => ({
      ...prev,
      layers: prev.layers.map((l) => (l.id === layerId ? { ...l, color } : l)),
      activeColor: prev.activeLayerId === layerId ? color : prev.activeColor,
    }))
  }, [])

  // Change color updates all layer objects color to the new layer color
  const setLayerColorAndUpdateObjects = useCallback((layerId: string, color: string) => {
    setState((prev) => {
      undoStack.current.push(JSON.parse(JSON.stringify(prev.layers)))
      if (undoStack.current.length > MAX_UNDO) undoStack.current.shift()
      redoStack.current = []
      return {
        ...prev,
        layers: prev.layers.map((l) =>
          l.id === layerId
            ? { ...l, color, objects: l.objects.map((o) => ({ ...o, color })) }
            : l
        ),
        activeColor: prev.activeLayerId === layerId ? color : prev.activeColor,
      }
    })
  }, [])

  // Reorder layers (drag and drop)
  const reorderLayers = useCallback((fromIndex: number, toIndex: number) => {
    pushUndo()
    setState((prev) => {
      const newLayers = [...prev.layers]
      const [removed] = newLayers.splice(fromIndex, 1)
      newLayers.splice(toIndex, 0, removed)
      return { ...prev, layers: newLayers }
    })
  }, [pushUndo])

  // Reorder objects within a layer (drag and drop)
  const reorderObjectsInLayer = useCallback((layerId: string, fromIndex: number, toIndex: number) => {
    pushUndo()
    setState((prev) => ({
      ...prev,
      layers: prev.layers.map((l) => {
        if (l.id !== layerId) return l
        const newObjects = [...l.objects]
        const [removed] = newObjects.splice(fromIndex, 1)
        newObjects.splice(toIndex, 0, removed)
        return { ...l, objects: newObjects }
      }),
    }))
  }, [pushUndo])

  // Move object from one layer to another (keeps original color)
  const moveObjectToLayer = useCallback((fromLayerId: string, objectId: string, toLayerId: string) => {
    if (fromLayerId === toLayerId) return
    pushUndo()
    setState((prev) => {
      const fromLayer = prev.layers.find(l => l.id === fromLayerId)
      const toLayer = prev.layers.find(l => l.id === toLayerId)
      if (!fromLayer || !toLayer) return prev
      const objIndex = fromLayer.objects.findIndex(o => o.id === objectId)
      if (objIndex === -1) return prev
      const obj = { ...fromLayer.objects[objIndex], layerId: toLayerId }
      return {
        ...prev,
        layers: prev.layers.map(l => {
          if (l.id === fromLayerId) return { ...l, objects: l.objects.filter(o => o.id !== objectId) }
          if (l.id === toLayerId) return { ...l, objects: [...l.objects, obj] }
          return l
        })
      }
    })
  }, [pushUndo])

  const addObjectToLayer = useCallback(
    (layerId: string, object: Omit<DrawObject, "id" | "layerId">) => {
      pushUndo()
      const newObj: DrawObject = {
        ...object,
        id: generateId(),
        layerId,
      }
      setState((prev) => ({
        ...prev,
        layers: prev.layers.map((l) =>
          l.id === layerId ? { ...l, objects: [...l.objects, newObj] } : l
        ),
      }))
      return newObj.id
    },
    [pushUndo]
  )

  const removeObject = useCallback((layerId: string, objectId: string) => {
    pushUndo()
    setState((prev) => ({
      ...prev,
      layers: prev.layers.map((l) =>
        l.id === layerId
          ? { ...l, objects: l.objects.filter((o) => o.id !== objectId) }
          : l
      ),
    }))
  }, [pushUndo])

  const updateObject = useCallback(
    (layerId: string, objectId: string, partial: Partial<DrawObject>) => {
      setState((prev) => ({
        ...prev,
        layers: prev.layers.map((l) =>
          l.id === layerId
            ? {
              ...l,
              objects: l.objects.map((o) =>
                o.id === objectId ? { ...o, ...partial } : o
              ),
            }
            : l
        ),
      }))
    },
    []
  )

  // Version that pushes undo (for discrete edits like vertex insert/remove)
  const updateObjectWithUndo = useCallback(
    (layerId: string, objectId: string, partial: Partial<DrawObject>) => {
      pushUndo()
      setState((prev) => ({
        ...prev,
        layers: prev.layers.map((l) =>
          l.id === layerId
            ? {
              ...l,
              objects: l.objects.map((o) =>
                o.id === objectId ? { ...o, ...partial } : o
              ),
            }
            : l
        ),
      }))
    },
    [pushUndo]
  )

  const setObjectComment = useCallback(
    (layerId: string, objectId: string, comment: string) => {
      pushUndo()
      setState((prev) => ({
        ...prev,
        layers: prev.layers.map((l) =>
          l.id === layerId
            ? {
              ...l,
              objects: l.objects.map((o) =>
                o.id === objectId ? { ...o, comment } : o
              ),
            }
            : l
        ),
      }))
    },
    [pushUndo]
  )

  const setPcbImage = useCallback((image: string | null) => {
    setState((prev) => ({ ...prev, pcbImage: image }))
  }, [])

  const setZoom = useCallback((zoom: number) => {
    setState((prev) => ({ ...prev, zoom: Math.max(0.1, Math.min(10, zoom)) }))
  }, [])

  const setPanOffset = useCallback((offset: Point) => {
    setState((prev) => ({ ...prev, panOffset: offset }))
  }, [])

  const resetViewToFit = useCallback((containerWidth: number, containerHeight: number, imageWidth: number, imageHeight: number) => {
    const padding = 40
    const availW = containerWidth - padding * 2
    const availH = containerHeight - padding * 2
    const scaleX = availW / imageWidth
    const scaleY = availH / imageHeight
    const newZoom = Math.max(0.1, Math.min(10, Math.min(scaleX, scaleY)))
    const cx = (containerWidth - imageWidth * newZoom) / 2
    const cy = (containerHeight - imageHeight * newZoom) / 2
    setState((prev) => ({ ...prev, zoom: newZoom, panOffset: { x: cx, y: cy } }))
  }, [])

  const clearLayer = useCallback((layerId: string) => {
    pushUndo()
    setState((prev) => ({
      ...prev,
      layers: prev.layers.map((l) =>
        l.id === layerId ? { ...l, objects: [] } : l
      ),
    }))
  }, [pushUndo])

  const setProjectProperties = useCallback((props: Partial<ProjectProperties>) => {
    setState((prev) => ({
      ...prev,
      projectProperties: { ...prev.projectProperties, ...props },
    }))
  }, [])

  const newProject = useCallback(() => {
    const newLayer: Layer = {
      id: generateId(),
      name: "Top Copper",
      color: LAYER_COLORS[0],
      visible: true,
      locked: false,
      opacity: 1,
      objects: [],
    }
    setState({
      layers: [newLayer],
      activeLayerId: newLayer.id,
      activeTool: "select",
      activeColor: LAYER_COLORS[0],
      strokeWidth: 2,
      activeLineStyle: "solid" as LineStyle,
      imageGrayscale: 0,
      imageTint: 0,
      imageOverlay: "",
      imageOverlayOpacity: 0,
      pcbImage: null,
      zoom: 1,
      panOffset: { x: 0, y: 0 },
      projectProperties: { ...DEFAULT_PROJECT_PROPERTIES },
    })
    undoStack.current = []
    redoStack.current = []
  }, [])

  const getExportFileName = useCallback((ext: string) => {
    const name = state.projectProperties.projectName || "Untitled"
    const now = new Date()
    const pad = (n: number) => String(n).padStart(2, "0")
    const dt = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`
    return `PCB_RE_${name.replace(/\s+/g, "_")}_${dt}.${ext}`
  }, [state.projectProperties.projectName])

  const exportJSON = useCallback((): string => {
    return JSON.stringify(
      serializeProject(state.layers, state.pcbImage, state.projectProperties, state.imageGrayscale, state.imageTint, state.imageOverlay, state.imageOverlayOpacity, state.zoom, state.panOffset),
      null,
      2
    )
  }, [state.layers, state.pcbImage, state.projectProperties, state.imageGrayscale, state.imageTint, state.imageOverlay, state.imageOverlayOpacity, state.zoom, state.panOffset])

  const importJSON = useCallback((jsonStr: string) => {
    try {
      const parsed = JSON.parse(jsonStr)
      const { layers, pcbImage, projectProperties, imageGrayscale, imageTint, imageOverlay, imageOverlayOpacity } = deserializeProject(parsed)
      if (layers.length === 0) return false
      pushUndo()
      setState((prev) => ({
        ...prev,
        layers,
        activeLayerId: layers[0].id,
        activeColor: layers[0].color,
        pcbImage: pcbImage ?? prev.pcbImage,
        projectProperties: projectProperties ?? prev.projectProperties,
        imageGrayscale: imageGrayscale ?? prev.imageGrayscale,
        imageTint: imageTint ?? prev.imageTint,
        imageOverlay: imageOverlay ?? prev.imageOverlay,
        imageOverlayOpacity: imageOverlayOpacity ?? prev.imageOverlayOpacity,
      }))
      return true
    } catch {
      return false
    }
  }, [pushUndo])

  const activeLayer =
    state.layers.find((l) => l.id === state.activeLayerId) ?? null

  return {
    ...state,
    activeLayer,
    canUndo,
    canRedo,
    undo,
    redo,
    pushUndo,
    setActiveTool,
    setActiveColor,
    setStrokeWidth,
    setActiveLineStyle,
    setImageGrayscale,
    setImageTint,
    setImageOverlay,
    setImageOverlayOpacity,
    setActiveLayer,
    addLayer,
    removeLayer,
    renameLayer,
    toggleLayerVisibility,
    toggleLayerLock,
    setLayerOpacity,
    setLayerColor,
    setLayerColorAndUpdateObjects,
    addObjectToLayer,
    removeObject,
    updateObject,
    updateObjectWithUndo,
    setObjectComment,
    setPcbImage,
    setZoom,
    setPanOffset,
    resetViewToFit,
    reorderLayers,
  reorderObjectsInLayer,
  moveObjectToLayer,
  clearLayer,
    setProjectProperties,
    newProject,
    getExportFileName,
    exportJSON,
    importJSON,
  }
}

export type PCBStore = ReturnType<typeof usePCBStore>
